/**
 * Learning Journey — 6 Ability Dimensions x 6 CEFR Levels
 *
 * Shows the recommended training weight distribution across
 * 6 ability dimensions for each CEFR level, with the learning
 * modes and features unlocked at each stage.
 */

import React, { useState, useMemo, useCallback } from 'react';
import './LearningJourneyPage.css';

// ── Data types ──────────────────────────────────────────────────
interface DimensionLevel {
  level: string;
  weight: number;
  modes: string[];
  locked?: string[];
  milestone?: string;
  detail?: string;
}

interface AbilityDimension {
  id: string;
  name: string;
  nameEn: string;
  subtitle: string;
  color: string;
  colorLight: string;
  levels: DimensionLevel[];
}

// ── Constants ───────────────────────────────────────────────────
const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const;

const CEFR_COLORS: Record<string, string> = {
  A1: '#16a34a', A2: '#22c55e',
  B1: '#ea580c', B2: '#f97316',
  C1: '#dc2626', C2: '#b91c1c',
};

const CEFR_LABELS: Record<string, string> = {
  A1: 'Breakthrough', A2: 'Waystage',
  B1: 'Threshold', B2: 'Vantage',
  C1: 'Advanced', C2: 'Mastery',
};

// ── 6 Ability Dimensions with target weights per CEFR level ─────
const DIMENSIONS: AbilityDimension[] = [
  {
    id: 'vocabulary',
    name: '詞彙',
    nameEn: 'Vocabulary',
    subtitle: '認識、理解、運用單字',
    color: '#8b5cf6',
    colorLight: '#f5f3ff',
    levels: [
      { level: 'A1', weight: 20, modes: ['單字模式', '閃卡（圖像聯想）'], detail: '生存詞彙 300-500 字、Sight Words', milestone: '掌握 300 基礎詞彙' },
      { level: 'A2', weight: 20, modes: ['單字模式', '閃卡（詞性辨識）', '基礎屈折 -s/-ed/-ing'], detail: '日常詞彙擴展、開始認識詞形變化' },
      { level: 'B1', weight: 15, modes: ['單字（字根字首）', '閃卡（派生詞）', '克漏字（字根提示）'], detail: '高頻字首字尾 un-/re-/dis-/-tion/-able', milestone: '掌握 15+ 字首字尾' },
      { level: 'B2', weight: 15, modes: ['單字（學術詞彙）', '測驗（拆字推義）', '拉丁/希臘字根'], detail: '系統性字根學習、AWL 學術詞彙', milestone: '掌握 20+ 拉丁字根' },
      { level: 'C1', weight: 10, modes: ['同源詞辨析', '詞彙層次意識'], detail: '日耳曼 vs 拉丁 vs 希臘詞彙層次' },
      { level: 'C2', weight: 5, modes: ['詞源學融入閱讀', '精準用詞'], detail: '近義詞微妙差異、修辭用詞' },
    ],
  },
  {
    id: 'grammar',
    name: '文法',
    nameEn: 'Grammar',
    subtitle: '句法規則、時態、結構',
    color: '#3b82f6',
    colorLight: '#eff6ff',
    levels: [
      { level: 'A1', weight: 10, modes: ['測驗（基礎選擇題）'], detail: 'be 動詞、簡單現在式、there is/are', locked: ['句意模式'] },
      { level: 'A2', weight: 15, modes: ['句意（基礎 graph）', '測驗'], detail: '過去式、助動詞、比較級、進行式' },
      { level: 'B1', weight: 15, modes: ['句意（語法標記）', '測驗', '造句'], detail: '關係子句、條件句、間接問句、被動語態', milestone: '間接問句正確率 > 70%' },
      { level: 'B2', weight: 10, modes: ['句意（進階分析）', '測驗（情境應用）'], detail: '假設語氣、倒裝句、分裂句' },
      { level: 'C1', weight: 10, modes: ['句意（修辭結構）', '寫作回饋'], detail: '語用意識、正式/非正式語法切換' },
      { level: 'C2', weight: 5, modes: ['修辭文法', '文體風格分析'], detail: '修辭文法、語法即風格' },
    ],
  },
  {
    id: 'sentencePatterns',
    name: '句型',
    nameEn: 'Sentence Patterns',
    subtitle: '語塊、搭配詞、固定表達',
    color: '#0891b2',
    colorLight: '#ecfeff',
    levels: [
      { level: 'A1', weight: 5, modes: ['閃卡（固定表達）'], detail: '高頻固定表達：How are you, Nice to meet you', locked: ['造句', '句意語塊標記'] },
      { level: 'A2', weight: 10, modes: ['克漏字（搭配詞版）', '句意（基礎）'], detail: '搭配詞啟蒙：make a decision, take a shower' },
      { level: 'B1', weight: 15, modes: ['句意（語塊標記）', '造句（指定語塊）', '搭配詞克漏字'], detail: '語塊系統學習、dependency graph 語塊邊界', milestone: '搭配詞正確率 > 70%' },
      { level: 'B2', weight: 15, modes: ['學術語塊', '全文（語塊標記閱讀）', '造句（複合句）'], detail: '學術語塊 + 搭配詞深化', milestone: '搭配詞正確率 > 80%' },
      { level: 'C1', weight: 15, modes: ['慣用語', '修辭語塊', '語域切換'], detail: '正式/非正式語塊、修辭表達' },
      { level: 'C2', weight: 10, modes: ['風格化語塊', '近義語塊精準選擇'], detail: '近母語級的語塊運用' },
    ],
  },
  {
    id: 'listening',
    name: '聽力',
    nameEn: 'Listening',
    subtitle: '聽辨、解碼、理解',
    color: '#059669',
    colorLight: '#ecfdf5',
    levels: [
      { level: 'A1', weight: 30, modes: ['聽寫（初級）', '全文（慢速聽讀）', '閃卡（聽音辨字）'], detail: 'Phonics 基礎、語速 0.75x、CVC 拼讀', milestone: '短母音辨識率 > 80%' },
      { level: 'A2', weight: 20, modes: ['聽寫（初→中級）', '全文（正常速）'], detail: '母音組合、R 控制母音、語速 0.85x' },
      { level: 'B1', weight: 15, modes: ['聽寫（中級）', '全文（計時閱讀）'], detail: '語速 1.0x、只顯示單字數量', milestone: '聽寫正確率 > 75%' },
      { level: 'B2', weight: 10, modes: ['聽寫（高級）', '精讀模式'], detail: '完全空白、語速 1.0-1.25x、連音弱化' },
      { level: 'C1', weight: 10, modes: ['聽寫（專家）', '多口音適應'], detail: '語速 1.25-1.5x、口音變體、專業領域' },
      { level: 'C2', weight: 15, modes: ['多口音聽寫', '即時理解'], detail: '所有口音、所有語速、所有領域' },
    ],
  },
  {
    id: 'speaking',
    name: '口說',
    nameEn: 'Speaking',
    subtitle: '發音、語調、流暢度',
    color: '#d97706',
    colorLight: '#fffbeb',
    levels: [
      { level: 'A1', weight: 25, modes: ['Echo 跟讀（慢速）'], detail: '逐句模仿、句間暫停 3 秒、只評發音', locked: ['Shadowing', '4/3/2 口語'] },
      { level: 'A2', weight: 20, modes: ['Echo 跟讀（評語調）', '語塊朗讀'], detail: '句間暫停 2 秒、以語塊為單位停頓' },
      { level: 'B1', weight: 15, modes: ['Shadowing 模式', '4/3/2 口語技巧'], detail: '邊聽邊說不暫停、評流暢度和跟隨度', milestone: 'Shadowing 跟隨度 > 60%' },
      { level: 'B2', weight: 10, modes: ['Shadowing（真實語速）', '口語表達'], detail: '含連音弱化的真實語料' },
      { level: 'C1', weight: 15, modes: ['語調訓練', '修辭朗讀'], detail: '升調/降調表達不同語意' },
      { level: 'C2', weight: 15, modes: ['風格化朗讀', '修辭節奏掌控'], detail: '近母語級的語音控制' },
    ],
  },
  {
    id: 'writing',
    name: '寫作',
    nameEn: 'Writing',
    subtitle: '克漏字 → 造句 → 擴寫 → 作文',
    color: '#dc2626',
    colorLight: '#fef2f2',
    levels: [
      { level: 'A1', weight: 10, modes: ['克漏字（有字庫）'], detail: '選項=空格數、3 分鐘限時', locked: ['造句', '擴寫', '作文'] },
      { level: 'A2', weight: 15, modes: ['克漏字（多干擾選項）', '造句（2 句合併）'], detail: '給連接詞選項、8-15 字', locked: ['擴寫', '作文'] },
      { level: 'B1', weight: 25, modes: ['克漏字（搭配詞）', '造句（3 句）', '擴寫（80-120 字）'], detail: '給明確 3 段結構', locked: ['作文'], milestone: '完成首篇擴寫 80+ 分' },
      { level: 'B2', weight: 40, modes: ['克漏字（純輸入）', '造句（3-4 句）', '擴寫（150-250 字）', '作文（Email/短文）'], detail: 'B2 是全面產出的起點', milestone: '首篇作文 90+ 分' },
      { level: 'C1', weight: 40, modes: ['造句（4 句）', '擴寫（250-400 字）', '作文（報告/論說文）'], detail: '學術/專業寫作、語域切換' },
      { level: 'C2', weight: 50, modes: ['擴寫（400+ 字）', '作文（學術論文/評論）'], detail: '修辭策略、風格化表達' },
    ],
  },
];

// ── Component ───────────────────────────────────────────────────
const LearningJourneyPage: React.FC = () => {
  const [selectedDim, setSelectedDim] = useState<string | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'heatmap' | 'flow'>('heatmap');

  const selectedCell = useMemo(() => {
    if (!selectedDim || !selectedLevel) return null;
    const dim = DIMENSIONS.find(d => d.id === selectedDim);
    if (!dim) return null;
    const lvl = dim.levels.find(l => l.level === selectedLevel);
    if (!lvl) return null;
    return { dim, level: lvl };
  }, [selectedDim, selectedLevel]);

  const handleCellClick = useCallback((dimId: string, level: string) => {
    if (selectedDim === dimId && selectedLevel === level) {
      setSelectedDim(null);
      setSelectedLevel(null);
    } else {
      setSelectedDim(dimId);
      setSelectedLevel(level);
    }
  }, [selectedDim, selectedLevel]);

  const maxWeight = 50;

  return (
    <div className="lj-page">
      <header className="lj-header">
        <div className="lj-header-left">
          <h1>Learning Journey</h1>
          <span className="lj-header-subtitle">6 能力維度 x 6 CEFR 等級</span>
        </div>
        <div className="lj-header-controls">
          <button
            className={viewMode === 'heatmap' ? 'active' : ''}
            onClick={() => setViewMode('heatmap')}
          >
            Heatmap
          </button>
          <button
            className={viewMode === 'flow' ? 'active' : ''}
            onClick={() => setViewMode('flow')}
          >
            Flow
          </button>
        </div>
      </header>

      <div className="lj-body">
        <div className="lj-main">
          {viewMode === 'heatmap' ? (
            <HeatmapView
              selectedDim={selectedDim}
              selectedLevel={selectedLevel}
              maxWeight={maxWeight}
              onCellClick={handleCellClick}
            />
          ) : (
            <FlowView
              selectedDim={selectedDim}
              onDimSelect={setSelectedDim}
            />
          )}
        </div>

        <aside className="lj-detail-panel">
          {selectedCell ? (
            <DetailPanel dim={selectedCell.dim} level={selectedCell.level} />
          ) : selectedDim && viewMode === 'flow' ? (
            <DimOverview dim={DIMENSIONS.find(d => d.id === selectedDim)!} />
          ) : (
            <EmptyDetail />
          )}
        </aside>
      </div>
    </div>
  );
};

// ── Heatmap View ────────────────────────────────────────────────
const HeatmapView: React.FC<{
  selectedDim: string | null;
  selectedLevel: string | null;
  maxWeight: number;
  onCellClick: (dimId: string, level: string) => void;
}> = ({ selectedDim, selectedLevel, maxWeight, onCellClick }) => {
  return (
    <div className="lj-heatmap">
      <div className="lj-heatmap-grid">
        <div className="lj-heatmap-corner" />
        {CEFR_LEVELS.map(level => (
          <div key={level} className="lj-heatmap-col-header">
            <span className="lj-col-level" style={{ color: CEFR_COLORS[level] }}>{level}</span>
            <span className="lj-col-label">{CEFR_LABELS[level]}</span>
          </div>
        ))}

        {DIMENSIONS.map(dim => (
          <React.Fragment key={dim.id}>
            <div className="lj-heatmap-row-header">
              <div className="lj-row-indicator" style={{ background: dim.color }} />
              <div className="lj-row-names">
                <span className="lj-row-name">{dim.name}</span>
                <span className="lj-row-name-en">{dim.nameEn}</span>
              </div>
            </div>

            {dim.levels.map(lvl => {
              const isSelected = selectedDim === dim.id && selectedLevel === lvl.level;
              const opacity = lvl.weight === 0 ? 0.02 : 0.04 + (lvl.weight / maxWeight) * 0.14;
              const barWidth = (lvl.weight / maxWeight) * 100;

              return (
                <div
                  key={lvl.level}
                  className={`lj-heatmap-cell ${isSelected ? 'selected' : ''} ${lvl.weight === 0 ? 'empty' : ''}`}
                  onClick={() => onCellClick(dim.id, lvl.level)}
                >
                  <div
                    className="lj-cell-fill"
                    style={{ background: dim.color, opacity }}
                  />
                  <div className="lj-cell-content">
                    <span className="lj-cell-pct">{lvl.weight}%</span>
                    <div
                      className="lj-cell-bar"
                      style={{ width: `${barWidth}%`, background: dim.color }}
                    />
                    {lvl.milestone && <span className="lj-cell-milestone-dot" />}
                  </div>
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>

      <div className="lj-heatmap-legend">
        <span className="lj-legend-label">訓練比重</span>
        <div className="lj-legend-scale">
          <span className="lj-legend-low">0%</span>
          <div className="lj-legend-gradient" />
          <span className="lj-legend-high">50%</span>
        </div>
        <span className="lj-legend-dot-hint">
          <span className="lj-cell-milestone-dot inline" /> 里程碑
        </span>
      </div>
    </div>
  );
};

// ── Flow View ───────────────────────────────────────────────────
const FlowView: React.FC<{
  selectedDim: string | null;
  onDimSelect: (dimId: string | null) => void;
}> = ({ selectedDim, onDimSelect }) => {
  return (
    <div className="lj-flow">
      {DIMENSIONS.map(dim => {
        const isSelected = selectedDim === dim.id;
        return (
          <div
            key={dim.id}
            className={`lj-flow-track ${isSelected ? 'selected' : ''}`}
            onClick={() => onDimSelect(isSelected ? null : dim.id)}
          >
            <div className="lj-flow-track-header" style={{ borderColor: dim.color }}>
              <div className="lj-flow-indicator" style={{ background: dim.color }} />
              <div>
                <span className="lj-flow-name">{dim.name}</span>
                <span className="lj-flow-name-en">{dim.nameEn}</span>
              </div>
            </div>

            <div className="lj-flow-levels">
              {dim.levels.map((lvl, i) => (
                <React.Fragment key={lvl.level}>
                  {i > 0 && <div className="lj-flow-connector" style={{ background: dim.color }} />}
                  <div
                    className={`lj-flow-level-node ${lvl.weight === 0 ? 'inactive' : ''}`}
                    style={{
                      borderColor: lvl.weight > 0 ? dim.color : '#e2e8f0',
                      background: lvl.weight > 0 ? dim.colorLight : 'transparent',
                    }}
                  >
                    <span className="lj-flow-level-name" style={{ color: CEFR_COLORS[lvl.level] }}>
                      {lvl.level}
                    </span>
                    <span className="lj-flow-level-weight">{lvl.weight}%</span>
                    {lvl.modes.length > 0 && (
                      <div className="lj-flow-features">
                        {lvl.modes.slice(0, 2).map(m => (
                          <span key={m} className="lj-flow-feature-tag">{m}</span>
                        ))}
                        {lvl.modes.length > 2 && (
                          <span className="lj-flow-feature-more">+{lvl.modes.length - 2}</span>
                        )}
                      </div>
                    )}
                    {lvl.milestone && <span className="lj-flow-milestone" />}
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Detail Panel Components ─────────────────────────────────────
const DetailPanel: React.FC<{ dim: AbilityDimension; level: DimensionLevel }> = ({ dim, level }) => {
  return (
    <div className="lj-detail">
      <div className="lj-detail-header" style={{ borderColor: dim.color }}>
        <div className="lj-detail-indicator" style={{ background: dim.color }} />
        <div>
          <h3>{dim.name} <span style={{ color: CEFR_COLORS[level.level] }}>{level.level}</span></h3>
          <p className="lj-detail-subtitle">{dim.subtitle}</p>
        </div>
      </div>

      <div className="lj-detail-weight">
        <span className="lj-detail-weight-label">訓練比重</span>
        <div className="lj-detail-weight-bar-bg">
          <div
            className="lj-detail-weight-bar-fill"
            style={{ width: `${(level.weight / 50) * 100}%`, background: dim.color }}
          />
        </div>
        <span className="lj-detail-weight-value" style={{ color: dim.color }}>{level.weight}%</span>
      </div>

      {level.detail && (
        <div className="lj-detail-section">
          <h4>學習重點</h4>
          <p>{level.detail}</p>
        </div>
      )}

      {level.modes.length > 0 && (
        <div className="lj-detail-section">
          <h4>推薦學習模式</h4>
          <ul className="lj-detail-features">
            {level.modes.map(m => (
              <li key={m} className="lj-feature-unlocked">{m}</li>
            ))}
          </ul>
        </div>
      )}

      {level.locked && level.locked.length > 0 && (
        <div className="lj-detail-section">
          <h4>尚未開放</h4>
          <ul className="lj-detail-features">
            {level.locked.map(m => (
              <li key={m} className="lj-feature-locked">{m}</li>
            ))}
          </ul>
        </div>
      )}

      {level.milestone && (
        <div className="lj-detail-milestone" style={{ borderColor: '#fde68a', background: '#fffbeb' }}>
          <span className="lj-milestone-icon" />
          <span>{level.milestone}</span>
        </div>
      )}
    </div>
  );
};

const DimOverview: React.FC<{ dim: AbilityDimension }> = ({ dim }) => {
  return (
    <div className="lj-detail">
      <div className="lj-detail-header" style={{ borderColor: dim.color }}>
        <div className="lj-detail-indicator" style={{ background: dim.color }} />
        <div>
          <h3>{dim.name}</h3>
          <p className="lj-detail-subtitle">{dim.nameEn} — {dim.subtitle}</p>
        </div>
      </div>

      <div className="lj-detail-section">
        <h4>各等級訓練比重</h4>
        <div className="lj-overview-weights">
          {dim.levels.map(lvl => (
            <div key={lvl.level} className="lj-overview-weight-row">
              <span className="lj-overview-level" style={{ color: CEFR_COLORS[lvl.level] }}>{lvl.level}</span>
              <div className="lj-overview-bar-bg">
                <div
                  className="lj-overview-bar-fill"
                  style={{ width: `${(lvl.weight / 50) * 100}%`, background: dim.color }}
                />
              </div>
              <span className="lj-overview-pct">{lvl.weight}%</span>
            </div>
          ))}
        </div>
      </div>

      <div className="lj-detail-section">
        <h4>里程碑</h4>
        <ul className="lj-detail-features">
          {dim.levels.filter(l => l.milestone).map(l => (
            <li key={l.level} className="lj-feature-milestone">
              <span style={{ color: CEFR_COLORS[l.level] }}>{l.level}</span> {l.milestone}
            </li>
          ))}
          {dim.levels.every(l => !l.milestone) && (
            <li className="lj-feature-locked">尚未定義里程碑</li>
          )}
        </ul>
      </div>
    </div>
  );
};

const EmptyDetail: React.FC = () => (
  <div className="lj-detail lj-detail-empty">
    <div className="lj-empty-icon">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M15 15l6 6M10 17a7 7 0 100-14 7 7 0 000 14z" />
      </svg>
    </div>
    <p>點擊格子或維度查看詳情</p>
    <div className="lj-empty-summary">
      <h4>學習旅程架構</h4>
      <p>
        6 個能力維度橫跨 CEFR A1–C2。每個等級根據學習階段調整訓練比重，並開放不同的學習模式。
      </p>
      <div className="lj-empty-tracks">
        {DIMENSIONS.map(d => (
          <div key={d.id} className="lj-empty-track-row">
            <div className="lj-empty-track-dot" style={{ background: d.color }} />
            <span className="lj-empty-track-name">{d.name}</span>
            <span className="lj-empty-track-sub">{d.subtitle}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default LearningJourneyPage;
